<script setup lang="ts">
import * as xss from 'xss';
import RichTextEditorWrapper from './RichTextEditorWrapper.vue';
import { templatesFn } from './templates';
import StorageService from '#layers/core/app/runtime/services/StorageService';
import Permissions from '#layers/core/shared/permissions';
import FileManager from '#layers/kan/app/components/filemanager/FileManager.vue';

const { $events } = useEvents();
const { $filters } = useFilters();
const { $permissions } = usePermissions();
const { $i18n } = useLocalization();
const theme = useThemeStore();
const aiStore = useAiSettingsStore();

const model = defineModel<string>({ default: '' });
const props = withDefaults(defineProps<{
    id: string;
    name?: string;
    disabled?: boolean;
    setup?: (editor: any) => void;
    options?: (options: any) => any;
    templates?: (templates: any[]) => any[];
}>(), {
    name: undefined,
    disabled: false,
    setup: (editor: any) => {},
    options: (options: any) => options,
    templates: (templates: any[]) => templates
});

const emit = defineEmits<{
    (e: 'input', value: string): void;
}>();

const xssWhiteList: any = {
    iframe: ['width', 'height', 'src', 'frameborder', 'allow', 'allowfullscreen'],
    source: ['src']
};

const xssOptions: xss.IFilterXSSOptions = {
    whiteList: {
        ...xss.getDefaultWhiteList(),
        ...xssWhiteList
    },
    onIgnoreTagAttr: (tag: string, name: string, value: string) =>
    {
        const allowed = ['id', 'style', 'class', 'contenteditable'];

        if (name.substring(0, 5) === 'data-' || allowed.includes(name))
        {
            return `${name}="${value}"`;
        }
    },
    onTagAttr: (tag: string, name: string, value: string) =>
    {
        return `${name}="${value}"`;
    }
};

const ready = ref(false);
const loaded = ref(false);
const editorContainer = ref<HTMLElement>(null);
const dialog = ref<any>(null);

const modal = ref(false);
const media = ref('files');
const filemanagerData: any = ref({
    enabled: false,
    callback: null,
    value: null,
    meta: null
});

const content = computed({
    get(): string
    {
        return filterXSS(model.value);
    },
    set(value: string)
    {
        updateModel(value);
    }
});

onMounted(async (): Promise<void> =>
{
    filemanagerData.value.enabled = await $permissions.all([Permissions.Storage.Access]);
    $events.$on('filemanager::modal::close', closeModal);
    $events.$on('filemanager::callback::value', callbackValue);
    ready.value = true;
});

onBeforeUnmount((): void =>
{
    $events.$off('filemanager::modal::close', closeModal);
    $events.$off('filemanager::callback::value', callbackValue);
});

function updateModel(content: string)
{
    const element = editorContainer.value?.querySelector('textarea') || editorContainer.value?.querySelector('input');

    element?.dispatchEvent(new Event('input', { bubbles: true }));

    model.value = content;
    emit('input', content);
}

function filterXSS(value: string)
{
    return xss.filterXSS(value, xssOptions);
};

function closeModal()
{
    modal.value = false;
}

function callbackValue(files: any[])
{
    if (modal.value)
    {
        const params = filemanagerData.value;

        if (files.length === 1)
        {
            const file = files.first();

            if (file)
            {
                const url = `#(storage):${file.publicId}`;
                const size = $filters.filesize(file.contentLength);
                const text = `${file.name} [${size}]`;

                if (params.meta.filetype === 'file')
                {
                    params.callback(url, { text: params.meta.text || text, title: file.name });

                    dialog.value.setData({
                        text: params.meta.text || text,
                        title: file.name
                    });
                }

                if (params.meta.filetype === 'image')
                {
                    const extIndex = file.name.lastIndexOf('.');
                    const alt = file.content[$i18n.shortLocale()]?.alt ?? (extIndex < 0 ? file.name : file.name.substring(0, extIndex));

                    params.callback(url, { alt, width: file.meta.width, height: file.meta.height });
                }

                if (params.meta.filetype === 'media')
                {
                    params.callback(url, { width: 480, height: 270 });
                }
            }
        }
        else
        {
            const url = `#(files):${files.map(el => el.publicId).join(',')}`;

            dialog.value.setData({
                images: { value: url, meta: {
                    images: files.map(({ url, meta }) => ({
                        url,
                        meta
                    }))
                } }
            });
        }

        closeModal();
    }
}

async function reinitializeEditor(): Promise<void>
{
    ready.value = false;
    loaded.value = false;

    await nextTick();

    ready.value = true;
}

function settingsFn()
{
    return {
        plugins: [
            'advlist', 'autolink', 'autosave', 'lists', 'image', 'link', 'charmap', 'anchor', 'autoresize',
            'searchreplace', 'wordcount', 'visualblocks', 'visualchars', 'code', 'template', 'codesample',
            'insertdatetime', 'media', 'nonbreaking', 'save', 'table', 'directionality', 'fullscreen', 'quickbars', 'emoticons'
        ],
        relative_urls: false,
        paste_data_images: false,
        browser_spellcheck: true,
        entity_encoding: 'raw',
        min_height: 450,
        autoresize_bottom_margin: 50,
        nowrap: false,
        resize: false,
        table_style_by_css: true,
        media_alt_source: false,
        media_poster: false,
        image_advtab: true,
        menubar: 'file edit view insert format tools table',
        menu: {
            insert: {
                title: 'Insert',
                items: 'link image media addcomment pageembed template codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor tableofcontents'
            }
        },
        toolbar: 'undo redo | format lineheight | align indent outdent | bullist numlist | charmap emoticons | anchor template | fullscreen | quicktable | wordcount',
        toolbar_groups: {
            format: {
                icon: 'format',
                tooltip: 'Formatting',
                items: 'bold italic underline strikethrough removeformat | subscript superscript | fontfamily blocks fontsize | forecolor backcolor'
            }
        },
        quickbars_selection_toolbar: `link unlink | styles fontsize | forecolor backcolor | lineheight | indent outdent`,
        quickbars_insert_toolbar: 'image link media template codesample quicktable | charmap emoticons hr | nonbreaking anchor',
        contextmenu: 'undo redo | visualchars visualblocks | insertoptions | charmap emoticons hr | nonbreaking anchor | styles fontfamily fontsize lineheight | forecolor backcolor | language | removeformat spellchecker spellcheckerlanguage',
        skin: theme.theme == 'dark' ? 'oxide-dark' : 'oxide'
    };
}

function init()
{
    const updateImage = (el: HTMLImageElement): void =>
    {
        const src = el.getAttribute('src');

        if (src.indexOf('#(storage):') == 0)
        {
            const publicId = src.replace('#(storage):', '');

            el.setAttribute('src', StorageService.previewResource(publicId));
            el.setAttribute('data-src', src);
        }
    };

    const updateVideo = (el: HTMLVideoElement): void =>
    {
        const sourceEl = el.querySelector('source');
        const src = sourceEl.getAttribute('src');

        if (src && src.indexOf('#(storage):') == 0)
        {
            const publicId = src.replace('#(storage):', '');

            el.setAttribute('src', StorageService.previewResource(publicId));
            el.setAttribute('data-src', src);
        }
    };

    const displayImages = (editor: any, content: string): string =>
    {
        const html = document.createElement('div');

        html.innerHTML = content;

        html.querySelectorAll('img').forEach((img) =>
        {
            updateImage(img);
        });

        return html.innerHTML;
    };

    const displayVideos = (editor: any, content: string): string =>
    {
        const html = document.createElement('div');

        html.innerHTML = content;

        html.querySelectorAll('video').forEach((video) =>
        {
            updateVideo(video);
        });

        return html.innerHTML;
    };

    const serializeImages = (editor: any, content: string): string =>
    {
        // Zablokowanie doczytywania obrazkow
        content = content.replace(new RegExp(' src="', 'g'), ' src-disabled="');

        const html = document.createElement('div');

        html.innerHTML = content;

        html.querySelectorAll('img').forEach((img) =>
        {
            const src = img.getAttribute('data-src');

            if (src && src.indexOf('#(storage):') == 0)
            {
                img.setAttribute('src-disabled', src);
                img.removeAttribute('data-src');
            }
        });

        // Przywrocenie oryginalnego kodu html
        return html.innerHTML.replace(new RegExp(' src-disabled="', 'g'), ' src="');
    };

    const serializeVideo = (editor: any, content: string): string =>
    {
        // Zablokowanie doczytywania wideo
        content = content.replace(new RegExp(' src="', 'g'), ' src-disabled="');

        const html = document.createElement('div');

        html.innerHTML = content;

        html.querySelectorAll('video').forEach((video) =>
        {
            const src = video.getAttribute('data-src');

            if (src && src.indexOf('#(storage):') == 0)
            {
                video.setAttribute('src-disabled', src);
                video.removeAttribute('data-src');
            }
        });

        // Przywrocenie oryginalnego kodu html
        return html.innerHTML.replace(new RegExp(' src-disabled="', 'g'), ' src="');
    };

    const filepickerCallback = (callback: any, val: any, meta: any): void =>
    {
        modal.value = true;
        filemanagerData.value.callback = callback;
        filemanagerData.value.value = val;
        filemanagerData.value.meta = meta;

        switch (meta.filetype)
        {
            case 'image':
                media.value = 'images';
                break;
            case 'media':
                media.value = 'videos';
                break;
            case 'file':
            default:
                media.value = 'files';
                break;
        }
    };

    return {
        ...props.options(settingsFn()),
        templates: props.templates(templatesFn()),
        language: $i18n.shortLocale(),
        file_picker_callback: filemanagerData.value.enabled ? filepickerCallback : null,
        setup: (editor: any) =>
        {
            editor.on('init', () =>
            {
                loaded.value = true;
            });

            editor.ui.registry.addContextMenu('insertoptions', {
                update: function (element: any)
                {
                    return 'image media template codesample inserttable';
                }
            });

            editor.on('BeforeSetContent', (e: any) =>
            {
                if (e.content)
                {
                    e.content = filterXSS(e.content);
                    e.content = displayImages(editor, e.content);
                    e.content = displayVideos(editor, e.content);
                }
            });

            editor.on('GetContent', (e: any) =>
            {
                if (e.content)
                {
                    e.content = serializeImages(editor, e.content);
                    e.content = serializeVideo(editor, e.content);
                }
            });

            editor.on('NodeChange', (e: any) =>
            {
                if (e.element && e.element.tagName == 'IMG')
                {
                    const el = e.element as HTMLImageElement;
                    const src = el.getAttribute('src');

                    if (src.startsWith('#(storage):'))
                    {
                        updateImage(el);
                    }
                }

                if (e.element && e.element.classList.contains('mce-object-video'))
                {
                    const el = e.element as HTMLImageElement;
                    const video = el.querySelector('video');

                    if (video && el.innerHTML.includes('#(storage):'))
                    {
                        updateVideo(video);
                    }
                }
            });

            editor.on('OpenWindow', (e: any) =>
            {
                const data = e.dialog.getData();

                dialog.value = e.dialog;

                // Image
                if (data?.src?.value?.length > 0 && data?.src?.value.startsWith(baseurl()))
                {
                    e.dialog.setData({
                        src: {
                            value: `#(storage):${data.src.value.split('/').pop()}`
                        }
                    });
                }
            });

            props.setup(editor);
        }
    };
}

watch(() => $i18n.shortLocale(), async (): Promise<void> =>
{
    await reinitializeEditor();
});

watch(() => theme.theme, async (): Promise<void> =>
{
    await reinitializeEditor();
});

watch(() => aiStore.aiSettings, async (settings, previousSettings): Promise<void> =>
{
    if (settings === previousSettings || !ready.value)
    {
        return;
    }

    await reinitializeEditor();
});
</script>

<template>
    <div v-show="loaded" ref="editorContainer" class="editor">
        <div v-if="ready">
            <RichTextEditorWrapper
                :id="props.id"
                v-model="content"
                src="/hugerte/hugerte.min.js"
                :name="props.name ?? props.id"
                :init="init()"
                :disabled="props.disabled"
            />
        </div>
        <Teleport v-if="modal" to="body">
            <div class="app-modal filemanager-modal">
                <FileManager :modal="true" :media="media" :value="filemanagerData.value" :max-files="0" />
            </div>
        </Teleport>
    </div>
</template>

<style lang="scss">
.editor {
    margin-left: 0;

    .modal {
        z-index: 65537 !important;
    }

    .tox-tinymce,
    .tox-hugerte {
        border: 1px solid var(--bs-border-color);
        border-radius: 4px;
    }
    .tox .tox-edit-area::before {
        border-radius: 0px;
        border: 1px solid var(--bs-primary-border-subtle);
    }
}

.tox {
    &-menu {
        max-height: 100vh !important;
        box-sizing: border-box !important;
    }
    &-promotion {
        display: none;
    }
    &-toolbar {
        &__group {
            border-color: var(--bs-gray-300) !important;
        }
    }
}
</style>
